import { useState, useEffect } from "react";
import API from "../api";

export default function Finance() {

  const [search, setSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    client: "",
    amount: "",
    type: "income",
    status: "pending"
  });

  /* ===============================
     FETCH
  =============================== */
  const fetchTransactions = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const res = await fetch(`${API}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      setTransactions(data.data || []);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  /* ===============================
     CREATE / UPDATE
  =============================== */
  const handleSubmit = async () => {
    try {
      if (!form.client || !form.amount) return alert("Fill all fields");

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const url = editMode
        ? `${API}/transactions/${form.id}`
        : `${API}/transactions`;

      const method = editMode ? "PUT" : "POST";

      await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      fetchTransactions();
      closeModal();

    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
     DELETE
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete transaction?")) return;

    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      await fetch(`${API}/transactions/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      setTransactions(prev => prev.filter(t => t.id !== id));

    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
     EDIT
  =============================== */
  const handleEdit = (t) => {
    setForm(t);
    setEditMode(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setForm({
      id: null,
      client: "",
      amount: "",
      type: "income",
      status: "pending"
    });
  };

  /* ===============================
     FILTER
  =============================== */
  const filtered = transactions.filter((t) =>
    t.client.toLowerCase().includes(search.toLowerCase())
  );

  /* ===============================
     KPI
  =============================== */
  const totalRevenue = transactions
    .filter(t => t.type === "income" && t.status === "paid")
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const pending = transactions
    .filter(t => t.status === "pending")
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const expenses = transactions
    .filter(t => t.type === "expense")
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const format = (val) => "₱" + Number(val).toLocaleString();

  /* ===============================
     STATUS STYLE
  =============================== */
  const getStatus = (status) => {
    if (status === "paid") return "bg-green-100 text-green-700";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Finance</h2>
          <p className="text-gray-500">
            Manage financial records, payments, and reports
          </p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg"
        >
          Add Transaction
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-6">

        <div className="bg-white p-6 border rounded">
          <h4>Total Revenue</h4>
          <p className="text-2xl">{format(totalRevenue)}</p>
        </div>

        <div className="bg-white p-6 border rounded text-yellow-600">
          <h4>Pending</h4>
          <p className="text-2xl">{format(pending)}</p>
        </div>

        <div className="bg-white p-6 border rounded text-red-600">
          <h4>Expenses</h4>
          <p className="text-2xl">{format(expenses)}</p>
        </div>

      </div>

      {/* SEARCH */}
      <input
        className="w-full p-3 border rounded"
        placeholder="Search transactions..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE */}
      <div className="bg-white border rounded-xl overflow-x-auto">

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No transactions found
          </div>
        ) : (
          <table className="w-full text-sm">

            <thead className="bg-gray-100">
              <tr>
                <th className="p-4">Client</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t hover:bg-gray-50">

                  <td className="p-4">{t.client}</td>
                  <td>{format(t.amount)}</td>
                  <td>{t.created_at?.slice(0,10)}</td>

                  <td>
                    <span className={`px-2 py-1 text-xs rounded ${getStatus(t.status)}`}>
                      {t.status}
                    </span>
                  </td>

                  <td className="flex gap-2 p-4">
                    <button
                      onClick={() => handleEdit(t)}
                      className="text-blue-600 text-sm"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(t.id)}
                      className="text-red-600 text-sm"
                    >
                      Delete
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        )}

      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">

          <div className="bg-white p-6 rounded-xl w-96 space-y-3">

            <h3 className="font-bold text-lg">
              {editMode ? "Edit Transaction" : "Add Transaction"}
            </h3>

            <input
              placeholder="Client"
              className="w-full p-2 border"
              value={form.client}
              onChange={(e)=>setForm({...form,client:e.target.value})}
            />

            <input
              placeholder="Amount"
              type="number"
              className="w-full p-2 border"
              value={form.amount}
              onChange={(e)=>setForm({...form,amount:e.target.value})}
            />

            <select
              className="w-full p-2 border"
              value={form.type}
              onChange={(e)=>setForm({...form,type:e.target.value})}
            >
              <option value="income">Income</option>
              <option value="expense">Expense</option>
            </select>

            <select
              className="w-full p-2 border"
              value={form.status}
              onChange={(e)=>setForm({...form,status:e.target.value})}
            >
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>

            <div className="flex justify-end gap-2">
              <button onClick={closeModal}>Cancel</button>

              <button
                onClick={handleSubmit}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                {editMode ? "Update" : "Save"}
              </button>
            </div>

          </div>

        </div>
      )}

    </div>
  );
}